import styled, { css } from 'styled-components';

import { StyledButton } from '../Button';
import type { PropsWithDefaults } from '../../types';
import { defaultThemeProp } from '../../theme';
import { StyledIcon } from '../Icon';
import { StyledCard } from '../Card';
import { StyledBackdrop } from '../Backdrop';
import { StyledResponsiveActionsContainer } from '../ResponsiveActions';
import { StyledProgressBackdrop } from '../Progress';
import { ellipsisOverflow } from '../../styles/mixins';

import type { ModalContextValue, ModalProps } from './Modal.types';

type ModalPropsWithDefaults = PropsWithDefaults<ModalProps, 'stretch' | 'center' | 'autoWidth'>;

export const StyledModalChildrenWrap = styled.div``;

export const StyledModalHeader = styled.header(({ theme: { base } }) => {
  const padAdjust = 0.75;

  return css`
    padding: calc(${3 - padAdjust} * ${base.spacing}) calc(${3 - padAdjust} * ${base.spacing})
      calc(${2.5 - padAdjust} * ${base.spacing}) calc(3 * ${base.spacing});

    @media (width < ${base.breakpoints.sm}) {
      padding: calc(${3 - padAdjust} * ${base.spacing}) calc(${1.5 - padAdjust} * ${base.spacing})
        calc(${2.5 - padAdjust} * ${base.spacing}) calc(1.5 * ${base.spacing});
    }

    h2 {
      margin-block-start: calc(0.5 * ${base.spacing});
    }

    h2 > ${StyledIcon} {
      margin-inline-end: ${base.spacing};
    }

    ${StyledButton} {
      color: ${base.palette['foreground-color']};
    }
  `;
});

StyledModalHeader.defaultProps = defaultThemeProp;

export const StyledModalContent = styled.div(({ theme: { base } }) => {
  return css`
    position: relative;
    padding: calc(0.5 * ${base.spacing}) calc(3 * ${base.spacing});
    overflow-anchor: none;

    @media (width < ${base.breakpoints.sm}) {
      padding: calc(0.5 * ${base.spacing}) calc(1.5 * ${base.spacing});
    }

    @media (height >= 31.25rem) {
      overflow-y: auto;
    }

    &:last-child {
      padding-bottom: calc(3 * ${base.spacing});
    }

    & > [data-config-root='true'] > ${StyledBackdrop} {
      top: var(--scroll-top, 0);
      height: 100%;
    }
  `;
});

StyledModalContent.defaultProps = defaultThemeProp;

export const StyledModal = styled(StyledCard)<
  ModalPropsWithDefaults & Pick<ModalContextValue, 'alert' | 'state'>
>(({ theme: { base, components }, alert, autoWidth, center, stretch, state }) => {
  let defaultMinWidth = '80ch';
  let defaultMaxWidth = '80ch';

  if (alert) {
    defaultMinWidth = '60ch';
    defaultMaxWidth = '60ch';
  } else if (autoWidth) {
    defaultMaxWidth = '100%';
  }

  return css`
    position: relative;
    box-shadow: ${base.shadow.high};

    @media (height < 31.25rem) {
      overflow-y: auto;
    }

    @media (forced-colors: active) {
      border: 0.0625rem solid transparent;
    }

    ${StyledResponsiveActionsContainer} {
      padding: calc(2.5 * ${base.spacing}) calc(3 * ${base.spacing}) calc(3 * ${base.spacing});
      @media (width < ${base.breakpoints.sm}) {
        padding: calc(5 * ${base.spacing}) calc(1.5 * ${base.spacing}) calc(1.5 * ${base.spacing});
      }
    }

    ${!alert && stretch
      ? css`
          height: calc(100% - 1rem);

          ${autoWidth
            ? css`
                max-width: min(calc(100% - 1rem), ${defaultMaxWidth});
              `
            : css`
                width: calc(100% - 1rem);
              `}

          @media (min-width: ${base.breakpoints.sm}) {
            height: calc(100% - 2rem);

            ${autoWidth
              ? css`
                  max-width: min(calc(100% - 2rem), ${defaultMaxWidth});
                `
              : css`
                  width: calc(100% - 2rem);
                `}
          }

          @media (min-width: ${base.breakpoints.md}) {
            height: calc(100% - 4rem);

            ${autoWidth
              ? css`
                  max-width: min(calc(100% - 4rem), ${defaultMaxWidth});
                `
              : css`
                  width: calc(100% - 4rem);
                `}
          }
        `
      : css`
          min-width: min(calc(100% - 1rem), ${defaultMinWidth});
          max-width: min(calc(100% - 1rem), ${defaultMaxWidth});
          max-height: calc(100% - 1rem);

          &:not(:has(footer)) ${StyledProgressBackdrop} {
            border-end-start-radius: ${base['border-radius']};
            border-end-end-radius: ${base['border-radius']};
          }

          ${!center &&
          css`
            margin-block-start: 0.5rem;
          `}

          @media (min-width: ${base.breakpoints.sm}) {
            min-width: min(calc(100% - 2rem), ${defaultMinWidth});
            max-width: min(calc(100% - 2rem), ${defaultMaxWidth});
            max-height: calc(100% - 2rem);
            ${!center &&
            css`
              margin-block-start: 1rem;
            `}
          }

          @media (min-width: ${base.breakpoints.md}) {
            min-width: min(calc(100% - 4rem), ${defaultMinWidth});
            max-width: min(calc(100% - 4rem), ${defaultMaxWidth});
            max-height: calc(100% - 4rem);
            ${!center &&
            css`
              margin-block-start: 2rem;
            `}
          }
        `}

    ${state === 'docked' &&
    css`
      /*
       * This seems like the most optimal of the suboptimal solutions.
       * https://styled-components.com/docs/faqs#how-can-i-override-styles-with-higher-specificity
       */
      &&& {
        /* Using non logical properties due to Safari 14.x support and since the inline bottom values are the same. */
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        pointer-events: auto;
      }
    `}

    &[aria-busy='true'] {
      ${StyledModalContent} {
        /* TODO: Replace with useScrollToggle hook. */
        overflow: hidden;
        min-height: 8rem;
      }
    }

    &:focus-visible {
      @media (forced-colors: none) {
        outline: none;
      }
      box-shadow: ${base.shadow.focus};
    }

    &&& {
      background: ${components.modal.background};
    }

    &[data-moveable] {
      position: absolute;

      ${StyledModalHeader} {
        cursor: grab;

        &:focus-visible {
          outline: none;
        }

        &:active {
          cursor: grabbing;
        }
      }

      &:has(${StyledModalHeader}:focus-visible) {
        box-shadow: ${base.shadow.high}, ${base.shadow.focus};
      }
    }
  `;
});

StyledModal.defaultProps = defaultThemeProp;

export const StyledMinimizedLabel = styled.span`
  flex-shrink: 1;
  ${ellipsisOverflow}
  font-weight: bold;
`;
